import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { financeAPI, unitAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Building2,
  ChevronDown,
  CreditCard,
  Download,
  Droplets,
  FileText,
  Info,
  ListChecks,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { formatMonthLabel, formatUnitCount } from '../utils/i18nFormatters';
import { formatStationDisplayId } from '../utils/stationId';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';

const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const abnormalStatusKeys = ['Failed', 'Pending', 'Cancelled'];

const defaultAbnormalFinance = {
  totalCount: 0,
  totalAmount: 0,
  statuses: abnormalStatusKeys.reduce((acc, status) => {
    acc[status] = { count: 0, amount: 0 };
    return acc;
  }, {}),
  recent: [],
  pagination: { page: 1, pageSize: 3, total: 0, totalPages: 0 }
};

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatInteger = (value) => Math.round(toNumber(value)).toLocaleString('en-PH');
const formatCurrency = (value) => `₱${formatInteger(value)}`;
const formatVolume = (value) => `${formatInteger(value)} L`;
const formatWaterVolume = (value) => `${toNumber(value).toLocaleString('en-PH', { maximumFractionDigits: 2 })} L`;

const normalizeKey = (value) => String(value || '').trim();

const getPeriodRange = ({ scope, year, month }) => {
  if (scope === 'year') {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`
    };
  }
  const paddedMonth = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: `${year}-${paddedMonth}-01`,
    endDate: `${year}-${paddedMonth}-${String(lastDay).padStart(2, '0')}`
  };
};

const getUnitDeviceId = (unit) => normalizeKey(unit.deviceId || unit.serialNumber || unit.unitId || unit.id || unit._id);

const getDeviceDisplayId = (value) => {
  const rawDeviceId = typeof value === 'string' || typeof value === 'number'
    ? value
    : value?.deviceId || value?.unit?.deviceId || value?.unitId || value?.serialNumber || value?.id || value?._id;
  return formatStationDisplayId({ deviceId: rawDeviceId });
};

const getUnitName = (unit, fallback) => (
  unit.deviceName
  || unit.name
  || unit.locationName
  || unit.stationName
  || fallback
);

const getUnitLocation = (unit) => (
  unit.locationName
  || unit.location
  || unit.address
  || unit.installationAddress
  || unit.stationAddress
  || ''
);

const getUnitPartner = (unit) => (
  unit.rpName
  || unit.kudName
  || unit.partnerName
  || unit.kud?.name
  || unit.rp?.name
  || ''
);

const normalizeAbnormalFinance = (value) => {
  if (!value || Array.isArray(value) || typeof value !== 'object') return defaultAbnormalFinance;
  return {
    totalCount: toNumber(value.totalCount),
    totalAmount: toNumber(value.totalAmount),
    statuses: abnormalStatusKeys.reduce((acc, status) => {
      acc[status] = {
        count: toNumber(value.statuses?.[status]?.count),
        amount: toNumber(value.statuses?.[status]?.amount)
      };
      return acc;
    }, {}),
    recent: Array.isArray(value.recent) ? value.recent : [],
    pagination: {
      page: Math.max(1, toNumber(value.pagination?.page) || 1),
      pageSize: Math.max(1, toNumber(value.pagination?.pageSize) || 3),
      total: toNumber(value.pagination?.total ?? value.totalCount),
      totalPages: Math.max(0, toNumber(value.pagination?.totalPages))
    }
  };
};

const getDeviceSummaryRows = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.data?.data)) return payload.data.data.data;
  if (Array.isArray(payload?.data?.machineSummary)) return payload.data.machineSummary;
  if (Array.isArray(payload?.data?.data?.machineSummary)) return payload.data.data.machineSummary;
  return [];
};

const buildDeviceFinanceRows = (units, summaries, fallbackText) => {
  const rowMap = new Map();
  const deviceIdByUnitId = new Map();

  (Array.isArray(units) ? units : []).forEach((unit, index) => {
    const deviceId = getUnitDeviceId(unit) || `unit-${index + 1}`;
    const unitId = normalizeKey(unit.id || unit.unitId);
    if (unitId) deviceIdByUnitId.set(unitId, deviceId);
    rowMap.set(deviceId, {
      key: deviceId,
      deviceId: getDeviceDisplayId(unit),
      fullDeviceId: deviceId,
      deviceName: getUnitName(unit, deviceId),
      location: getUnitLocation(unit) || fallbackText.unknownLocation,
      partner: getUnitPartner(unit) || fallbackText.unassigned,
      revenue: 0,
      transactionCount: 0,
      waterVolume: 0,
      period: '-'
    });
  });

  (Array.isArray(summaries) ? summaries : []).forEach((summary, index) => {
    const summaryUnitId = normalizeKey(summary.unitId);
    const deviceId = deviceIdByUnitId.get(summaryUnitId)
      || normalizeKey(summary.deviceId || summary.deviceName)
      || `summary-${index + 1}`;
    const existing = rowMap.get(deviceId) || {
      key: deviceId,
      deviceId: getDeviceDisplayId(summary),
      fullDeviceId: deviceId,
      deviceName: summary.deviceName || deviceId,
      location: summary.location || fallbackText.unknownLocation,
      partner: fallbackText.unassigned,
      revenue: 0,
      transactionCount: 0,
      waterVolume: 0,
      period: '-'
    };

    rowMap.set(deviceId, {
      ...existing,
      deviceName: summary.deviceName || existing.deviceName,
      location: summary.location || existing.location,
      revenue: toNumber(summary.salesAmount || summary.revenue || summary.amount),
      transactionCount: toNumber(summary.transactionCount || summary.count),
      waterVolume: toNumber(summary.waterVolume || summary.volume),
      period: summary.period || existing.period
    });
  });

  return Array.from(rowMap.values()).sort((a, b) => (
    b.revenue - a.revenue
    || b.transactionCount - a.transactionCount
    || a.fullDeviceId.localeCompare(b.fullDeviceId)
  ));
};

const downloadBlob = (data, filename) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function FinanceModuleCard({ card }) {
  const Icon = card.icon;
  const [showHint, setShowHint] = useState(false);
  const containerRef = useRef(null);
  const hintButtonRef = useRef(null);

  useEffect(() => {
    if (!showHint) return undefined;
    const closeOnOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setShowHint(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setShowHint(false);
        hintButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showHint]);

  return (
    <article
      ref={containerRef}
      className={`admin-card relative min-h-[230px] min-w-0 rounded-lg border-l-4 p-4 pb-14 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg sm:p-5 sm:pb-14 md:min-h-[270px] ${card.accentClass} ${card.layoutClass}`}
      data-testid="finance-module-card"
    >
      <Link
        to={card.href}
        className="absolute inset-0 z-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)] focus:ring-offset-2"
        aria-label={`${card.title} - ${card.linkLabel}`}
      />
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <h3 className="min-w-0 break-words text-sm font-black leading-5 text-[var(--admin-text-primary)]">
              {card.title}
            </h3>
            {card.hint && (
              <button
                ref={hintButtonRef}
                type="button"
                className="relative z-10 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] text-[var(--admin-text-muted)] transition hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)]"
                aria-label={card.hint}
                aria-expanded={showHint}
                onClick={() => setShowHint((value) => !value)}
              >
                <Info size={14} />
              </button>
            )}
          </div>
          <p className="mt-2 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{card.subText}</p>
        </div>
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--admin-surface-soft)] text-[var(--admin-accent)]">
          <Icon size={20} />
        </span>
      </div>
      {showHint && (
        <div className="admin-card absolute left-4 right-4 top-16 z-20 rounded-lg p-4 text-sm leading-6 shadow-xl" role="dialog" aria-label={card.title}>
          <p className="font-bold text-[var(--admin-text-primary)]">{card.hint}</p>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="break-words text-2xl font-black leading-tight text-[var(--admin-text-primary)] sm:text-3xl">
          {card.status === 'loading'
            ? <span className="inline-block h-8 w-36 animate-pulse rounded-lg bg-[var(--admin-surface-soft)]" aria-label={card.loadingLabel} />
            : card.value === null ? card.unavailableLabel : formatCurrency(card.value)}
        </div>
        {!['ready', 'loading'].includes(card.status) && (
          <span className={`rounded-full px-2 py-1 text-[11px] font-black ${card.status === 'unavailable' ? 'bg-amber-100 text-amber-900' : 'bg-sky-100 text-sky-900'}`}>
            {card.statusLabel}
          </span>
        )}
      </div>
      {card.details && (
        <div className="mt-4 space-y-2">
          {card.details.map((detail) => (
            <div key={detail.label} className="admin-muted-panel rounded-lg p-3">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="break-words text-sm font-bold text-[var(--admin-text-primary)]">{detail.label}</p>
                  {detail.hint && <p className="mt-1 break-words text-xs leading-5 text-[var(--admin-text-muted)]">{detail.hint}</p>}
                </div>
                <span className="shrink-0 text-right text-sm font-black text-[var(--admin-text-primary)]">
                  {detail.format === 'count'
                    ? formatInteger(detail.value)
                    : detail.format === 'volume'
                      ? formatWaterVolume(detail.value)
                      : formatCurrency(detail.value)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="pointer-events-none absolute bottom-4 right-4 flex items-center gap-1 text-xs font-black text-[var(--admin-accent)]">
        <span>{card.linkLabel}</span>
        <ArrowRight size={14} />
      </div>
    </article>
  );
}

export default function Finance() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const canViewUnits = hasAdminPermission(user, 'view_units');
  const now = new Date();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statementDate, setStatementDate] = useState({
    scope: searchParams.get('scope') === 'year' ? 'year' : 'month',
    year: Number(searchParams.get('year')) || now.getFullYear(),
    month: Number(searchParams.get('month')) || now.getMonth() + 1
  });
  const [dashboard, setDashboard] = useState(null);
  const [abnormalFinance, setAbnormalFinance] = useState(defaultAbnormalFinance);
  const [deviceFinanceRows, setDeviceFinanceRows] = useState([]);
  const [abnormalPage, setAbnormalPage] = useState(1);
  const [devicePage, setDevicePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [exportingReport, setExportingReport] = useState('');
  const [error, setError] = useState('');

  const selectedMonthLabel = statementDate.scope === 'year'
    ? String(statementDate.year)
    : `${statementDate.year}-${String(statementDate.month).padStart(2, '0')}`;

  const updateStatementDate = (next) => {
    setAbnormalPage(1);
    setDevicePage(1);
    setStatementDate((current) => {
      const value = { ...current, ...next };
      const params = new URLSearchParams(searchParams);
      params.set('scope', value.scope);
      params.set('year', String(value.year));
      if (value.scope === 'month') params.set('month', String(value.month));
      else params.delete('month');
      setSearchParams(params, { replace: true });
      return value;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const summaryParams = {
        ...getPeriodRange(statementDate),
        summaryPeriod: statementDate.scope
      };

      const [
        dashboardResult,
        unitsResult,
        abnormalFinanceResult,
        transactionSummaryResult
      ] = await Promise.allSettled([
        financeAPI.getDashboardSummary(statementDate),
        canViewUnits ? unitAPI.getUnits() : Promise.resolve({ data: { success: true, data: [] } }),
        financeAPI.getAbnormalFinanceSummary({ ...statementDate, page: abnormalPage, limit: 3 }),
        financeAPI.getTransactionSummary(summaryParams)
      ]);

      const failedRequired = [dashboardResult].find((result) => result.status === 'rejected');
      if (failedRequired) throw failedRequired.reason;

      setDashboard(dashboardResult.value.data?.success ? dashboardResult.value.data.data : null);

      const units = unitsResult.status === 'fulfilled' && unitsResult.value.data?.success && Array.isArray(unitsResult.value.data.data)
        ? unitsResult.value.data.data
        : [];
      const summaryRows = transactionSummaryResult.status === 'fulfilled'
        ? getDeviceSummaryRows(transactionSummaryResult.value)
        : [];

      if (transactionSummaryResult.status === 'rejected') {
        console.warn('Failed to fetch device transaction summary, device rows will use zero finance data:', transactionSummaryResult.reason);
      }

      setDeviceFinanceRows(buildDeviceFinanceRows(units, summaryRows, {
        unassigned: t('finance.table.unassigned'),
        unknownLocation: t('finance.table.unknownLocation')
      }));

      if (abnormalFinanceResult.status === 'fulfilled' && abnormalFinanceResult.value.data?.success) {
        setAbnormalFinance(normalizeAbnormalFinance(abnormalFinanceResult.value.data.data));
      } else {
        if (abnormalFinanceResult.status === 'rejected') {
          console.warn('Failed to fetch abnormal finance summary, finance data still loaded:', abnormalFinanceResult.reason);
        }
        setAbnormalFinance(defaultAbnormalFinance);
      }
    } catch (err) {
      console.error('Failed to fetch finance data:', err);
      setError(err.response?.data?.message || err.message || t('finance.errors.load'));
      setDeviceFinanceRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementDate.scope, statementDate.year, statementDate.month, abnormalPage, canViewUnits]);

  const deviceTotalPages = Math.ceil(deviceFinanceRows.length / 3);
  const visibleDeviceFinanceRows = deviceFinanceRows.slice((devicePage - 1) * 3, devicePage * 3);
  const abnormalTotalPages = abnormalFinance.pagination.totalPages || Math.ceil(abnormalFinance.totalCount / 3);

  const formatAbnormalTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-PH', {
      timeZone: 'Asia/Manila',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDownloadReport = async (kind) => {
    const params = {
      ...getPeriodRange(statementDate),
      summaryPeriod: statementDate.scope,
      lang: 'en'
    };
    setShowDownloadMenu(false);
    setExportingReport(kind);

    try {
      if (kind === 'summary' || kind === 'both') {
        const summaryRes = await financeAPI.exportTransactionSummary(params);
        downloadBlob(summaryRes.data, `ATMWater_Finance_Summary_${selectedMonthLabel}.csv`);
      }
      if (kind === 'detail' || kind === 'both') {
        const detailRes = await financeAPI.exportTransactions(params);
        downloadBlob(detailRes.data, `ATMWater_Finance_Detail_${selectedMonthLabel}.csv`);
      }
    } catch (err) {
      console.error('Failed to download finance report:', err);
      alert(t('finance.downloadMenu.error'));
    } finally {
      setExportingReport('');
    }
  };

  const financeModules = useMemo(() => {
    const modules = dashboard?.modules || {};
    const temporaryProductionIsolation = dashboard?.meta?.financeScope === 'temporary-live-production-isolation-payermax-only-topups';
    const query = `scope=${statementDate.scope}&year=${statementDate.year}${statementDate.scope === 'month' ? `&month=${statementDate.month}` : ''}`;
    const common = (module, index) => ({
      value: module?.amount ?? null,
      status: loading ? 'loading' : (module?.status || 'unavailable'),
      statusLabel: t(`finance.moduleStatus.${module?.status || 'unavailable'}`),
      unavailableLabel: t('finance.moduleStatus.noAmount'),
      loadingLabel: t('common.loading'),
      linkLabel: t('finance.viewDetails'),
      layoutClass: index < 4 ? 'xl:col-span-3' : 'xl:col-span-4'
    });

    return [
      {
        ...common(modules.physicalCardSales, 0),
        title: t('finance.sevenModules.physicalCardSales.title'),
        subText: t('finance.sevenModules.physicalCardSales.subtitle'),
        hint: t('finance.sevenModules.physicalCardSales.hint'),
        href: `/cards?view=sales&${query}`,
        accentClass: 'border-orange-400',
        icon: CreditCard
      },
      {
        ...common(modules.physicalCardBalance, 1),
        title: t('finance.sevenModules.physicalCardBalance.title'),
        subText: t('finance.sevenModules.physicalCardBalance.subtitle'),
        hint: t('finance.sevenModules.physicalCardBalance.hint'),
        href: `/cards?view=balance&${query}`,
        details: [
          { label: t('finance.sevenModules.physicalCardBalance.totalValue'), value: modules.physicalCardBalance?.totalValue || 0 },
          { label: t('finance.sevenModules.physicalCardBalance.usedBalance'), value: modules.physicalCardBalance?.usedBalance || 0 },
          {
            label: t('finance.sevenModules.physicalCardBalance.inventoryBalance'),
            value: modules.physicalCardBalance?.breakdown?.inventory?.balance || 0,
            hint: t('finance.sevenModules.physicalCardBalance.inventoryHint', {
              count: formatInteger(modules.physicalCardBalance?.breakdown?.inventory?.cardCount || 0)
            })
          }
        ],
        accentClass: 'border-cyan-400',
        icon: Wallet
      },
      {
        ...common(modules.appPrepayments, 2),
        title: t('finance.sevenModules.appPrepayments.title'),
        subText: t('finance.sevenModules.appPrepayments.subtitle', { period: selectedMonthLabel }),
        hint: temporaryProductionIsolation
          ? t('finance.productionIsolation.payermaxOnly')
          : t('finance.sevenModules.appPrepayments.hint'),
        href: `/transactions?financeType=prepayment&${query}`,
        details: [
          { label: t('finance.sevenModules.appPrepayments.payermaxPaid'), value: modules.appPrepayments?.breakdown?.payermaxPaidAmount || 0 },
          { label: t('finance.sevenModules.appPrepayments.payermaxCredited'), value: modules.appPrepayments?.breakdown?.payermaxCreditedAmount || 0 },
          ...(!temporaryProductionIsolation ? [
            { label: t('finance.sevenModules.appPrepayments.backendCredited'), value: modules.appPrepayments?.breakdown?.appCreditedAmount || 0 },
            { label: t('finance.sevenModules.appPrepayments.legacyTimeFallback'), value: modules.appPrepayments?.breakdown?.legacyTimeFallbackAmount || 0 }
          ] : []),
          { label: t('finance.sevenModules.appPrepayments.uncredited'), value: modules.appPrepayments?.breakdown?.uncreditedAmount || 0 },
          { label: t('finance.sevenModules.appPrepayments.exceptions'), value: modules.appPrepayments?.breakdown?.exceptionCount || 0, format: 'count' }
        ],
        accentClass: 'border-amber-500',
        icon: TrendingUp
      },
      {
        ...common(modules.deliveredWaterRevenue, 3),
        title: t('finance.sevenModules.deliveredWaterRevenue.title'),
        subText: t('finance.sevenModules.deliveredWaterRevenue.subtitle', { period: selectedMonthLabel }),
        hint: t('finance.sevenModules.deliveredWaterRevenue.hint'),
        href: `/transactions?financeType=water&${query}`,
        details: [
          { label: t('finance.sevenModules.deliveredWaterRevenue.deliveredVolume'), value: modules.deliveredWaterRevenue?.breakdown?.deliveredWaterVolume || 0, format: 'volume' },
          { label: t('finance.sevenModules.deliveredWaterRevenue.paidVolume'), value: modules.deliveredWaterRevenue?.breakdown?.paidWaterVolume || 0, format: 'volume' },
          { label: t('finance.sevenModules.deliveredWaterRevenue.excludedVolume'), value: modules.deliveredWaterRevenue?.breakdown?.excludedFundingWaterVolume || 0, format: 'volume' },
          { label: t('finance.sevenModules.deliveredWaterRevenue.transactionCount'), value: dashboard?.reconciliation?.paidTransactionCount || 0, format: 'count' },
          { label: t('finance.sevenModules.deliveredWaterRevenue.unbalancedCount'), value: dashboard?.reconciliation?.unbalancedTransactionCount || 0, format: 'count' }
        ],
        accentClass: 'border-emerald-500',
        icon: Droplets
      },
      {
        ...common(modules.softwareSubscriptionRevenue, 4),
        title: t('finance.sevenModules.softwareSubscriptionRevenue.title'),
        subText: t('finance.sevenModules.softwareSubscriptionRevenue.subtitle'),
        hint: t('finance.sevenModules.softwareSubscriptionRevenue.hint'),
        href: `/finance/subscriptions?${query}`,
        details: [
          { label: t('finance.sevenModules.softwareSubscriptionRevenue.deviceMonths'), value: modules.softwareSubscriptionRevenue?.billableDeviceMonths || 0, format: 'count' },
          { label: t('finance.sevenModules.softwareSubscriptionRevenue.feePerDevice'), value: modules.softwareSubscriptionRevenue?.feePerDeviceMonth || 0 },
          {
            label: t('finance.sevenModules.softwareSubscriptionRevenue.excludedDevices'),
            value: toNumber(modules.softwareSubscriptionRevenue?.excludedInactiveDevices) + toNumber(modules.softwareSubscriptionRevenue?.excludedNotInstalledDevices),
            format: 'count'
          }
        ],
        accentClass: 'border-violet-500',
        icon: ListChecks
      },
      {
        ...common(modules.allocatedRevenue, 5),
        title: t('finance.sevenModules.allocatedRevenue.title'),
        subText: t('finance.sevenModules.allocatedRevenue.subtitle'),
        hint: t('finance.sevenModules.allocatedRevenue.hint'),
        href: `/profit-sharing-finance?${query}`,
        details: [
          { label: t('finance.sevenModules.allocatedRevenue.steward'), value: modules.allocatedRevenue?.breakdown?.steward || 0 },
          { label: t('finance.sevenModules.allocatedRevenue.partnerPa'), value: modules.allocatedRevenue?.breakdown?.partnerPa || 0 },
          { label: t('finance.sevenModules.allocatedRevenue.regionalPartnerRp'), value: modules.allocatedRevenue?.breakdown?.regionalPartnerRp || 0 }
        ],
        accentClass: 'border-sky-400',
        icon: Activity
      },
      {
        ...common(modules.headquartersWaterRevenue, 6),
        title: t('finance.sevenModules.headquartersWaterRevenue.title'),
        subText: t('finance.sevenModules.headquartersWaterRevenue.subtitle'),
        hint: t('finance.sevenModules.headquartersWaterRevenue.hint', { difference: formatCurrency(dashboard?.reconciliation?.difference || 0) }),
        href: `/profit-sharing-finance?account=Headquarters&${query}`,
        details: [
          { label: t('finance.sevenModules.headquartersWaterRevenue.difference'), value: dashboard?.reconciliation?.difference || 0 },
          { label: t('finance.sevenModules.headquartersWaterRevenue.missingLedgerCount'), value: dashboard?.reconciliation?.missingLedgerCount || 0, format: 'count' },
          { label: t('finance.sevenModules.headquartersWaterRevenue.duplicateLedgerCount'), value: dashboard?.reconciliation?.duplicateLedgerCount || 0, format: 'count' }
        ],
        accentClass: 'border-indigo-500',
        icon: Building2
      }
    ];
  }, [dashboard, loading, selectedMonthLabel, statementDate, t]);

  const abnormalRows = useMemo(() => abnormalStatusKeys.map((status) => ({
    status,
    label: t(`finance.abnormal.status.${status}`),
    count: abnormalFinance.statuses?.[status]?.count || 0,
    amount: abnormalFinance.statuses?.[status]?.amount || 0
  })), [abnormalFinance, t]);

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h2 className="break-words text-2xl font-black leading-tight text-[var(--admin-text-primary)] sm:text-3xl">{t('finance.title')}</h2>
          <p className="mt-2 max-w-4xl break-words text-sm leading-6 text-[var(--admin-text-muted)]">{t('finance.companyViewNote')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex min-h-11 rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] p-1" aria-label={t('finance.period.scope')}>
            {['month', 'year'].map((scope) => (
              <button
                key={scope}
                type="button"
                onClick={() => updateStatementDate({ scope })}
                aria-pressed={statementDate.scope === scope}
                className={`rounded-md px-3 py-2 text-sm font-black transition ${statementDate.scope === scope ? 'bg-[var(--admin-accent)] text-slate-950' : 'text-[var(--admin-text-muted)] hover:text-[var(--admin-text-primary)]'}`}
              >
                {t(`finance.period.${scope}`)}
              </button>
            ))}
          </div>
          <select
            value={statementDate.year}
            aria-label={t('finance.period.year')}
            onChange={(event) => updateStatementDate({ year: Number(event.target.value) })}
            className="admin-input min-h-11 rounded-lg px-3 py-2 text-sm font-semibold outline-none"
          >
            {Array.from({ length: Math.max(1, now.getFullYear() - 2024) }, (_, index) => 2025 + index).map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
          {statementDate.scope === 'month' && (
            <select
              value={statementDate.month}
              aria-label={t('finance.period.monthLabel')}
              onChange={(event) => updateStatementDate({ month: Number(event.target.value) })}
              className="admin-input min-h-11 rounded-lg px-3 py-2 text-sm font-semibold outline-none"
            >
              {monthOptions.map((month) => (
                <option key={month} value={month}>{formatMonthLabel(month)}</option>
              ))}
            </select>
          )}
          <div className="relative">
            <button
              onClick={() => setShowDownloadMenu((prev) => !prev)}
              disabled={Boolean(exportingReport)}
              aria-expanded={showDownloadMenu}
              aria-haspopup="menu"
              className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] px-4 py-2 text-sm font-black text-[var(--admin-text-primary)] shadow-sm transition hover:border-[var(--admin-accent)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download size={16} />
              <span>{exportingReport ? t('finance.downloadMenu.exporting') : t('finance.downloadReport')}</span>
              <ChevronDown size={14} className={`transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} />
            </button>
            {showDownloadMenu && (
              <div
                className="admin-card absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-lg shadow-xl"
                role="menu"
              >
                <button
                  onClick={() => handleDownloadReport('summary')}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-[var(--admin-text-primary)] transition hover:bg-[var(--admin-surface-soft)]"
                  role="menuitem"
                >
                  <FileText size={16} className="text-[var(--admin-accent)]" />
                  {t('finance.downloadMenu.summary')}
                </button>
                <button
                  onClick={() => handleDownloadReport('detail')}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-[var(--admin-text-primary)] transition hover:bg-[var(--admin-surface-soft)]"
                  role="menuitem"
                >
                  <ListChecks size={16} className="text-emerald-500" />
                  {t('finance.downloadMenu.detail')}
                </button>
                <button
                  onClick={() => handleDownloadReport('both')}
                  className="flex w-full items-center gap-3 border-t border-[var(--admin-border)] px-4 py-3 text-left text-sm font-black text-[var(--admin-text-primary)] transition hover:bg-[var(--admin-surface-soft)]"
                  role="menuitem"
                >
                  <Download size={16} className="text-[var(--admin-accent)]" />
                  {t('finance.downloadMenu.both')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
          {error}
        </div>
      )}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-12" aria-label={t('finance.title')}>
        {financeModules.map((card) => (
          <FinanceModuleCard key={card.title} card={card} />
        ))}
      </section>

      <section className="admin-card rounded-lg" aria-label={t('finance.abnormal.title')}>
        <div className="flex flex-col gap-3 border-b border-[var(--admin-border)] p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <AlertCircle size={22} />
            </span>
            <div className="min-w-0">
              <h4 className="break-words text-base font-black text-[var(--admin-text-primary)]">{t('finance.abnormal.title')}</h4>
              <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{t('finance.abnormal.subtitle', { month: selectedMonthLabel })}</p>
            </div>
          </div>
          <div className="break-words text-sm font-black text-amber-700">
            {t('finance.abnormal.total', {
              count: abnormalFinance.totalCount || 0,
              amount: formatCurrency(abnormalFinance.totalAmount || 0)
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:p-5 md:grid-cols-3">
          {abnormalRows.map((row) => (
            <div key={row.status} className="admin-muted-panel rounded-lg p-4">
              <div className="flex min-w-0 items-center justify-between gap-3">
                <span className="break-words text-sm font-black text-[var(--admin-text-primary)]">{row.label}</span>
                <span className="shrink-0 rounded-lg bg-amber-100 px-2 py-1 text-xs font-black text-amber-800">
                  {t('finance.abnormal.count', { count: row.count })}
                </span>
              </div>
              <p className="mt-3 break-words text-xl font-black text-[var(--admin-text-primary)]">{formatCurrency(row.amount)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-[var(--admin-border)] p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3 text-xs font-black text-[var(--admin-text-muted)]">
            <span>{t('finance.abnormal.recent')}</span>
            <span>{t('finance.abnormal.pageSummary', { page: abnormalFinance.pagination.page, totalPages: abnormalTotalPages || 1 })}</span>
          </div>
          {abnormalFinance.recent.length === 0 ? (
            <div className="admin-muted-panel rounded-lg p-5 text-sm font-bold text-[var(--admin-text-muted)]">
              {t('finance.abnormal.empty')}
            </div>
          ) : (
            <>
              <div className="space-y-3 md:hidden">
                {abnormalFinance.recent.map((item) => (
                  <div key={item.id || item.externalId || item.payermax_order_id} className="admin-muted-panel rounded-lg p-4">
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-all font-mono text-xs font-bold text-[var(--admin-text-primary)]">{item.payermax_order_id || item.externalId || item.recordId || item.id || '-'}</p>
                        <p className="mt-1 text-xs text-[var(--admin-text-muted)]">{formatAbnormalTime(item.completedAt || item.createdAt)}</p>
                      </div>
                      <span className="shrink-0 rounded-lg bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">
                        {t(`finance.abnormal.status.${item.status}`, { defaultValue: item.status || '-' })}
                      </span>
                    </div>
                    <div className="mt-4 flex items-end justify-between gap-3">
                      <span
                        className="min-w-0 break-words text-xs text-[var(--admin-text-muted)]"
                        title={item.deviceId || item.unit?.deviceId || ''}
                      >
                        {getDeviceDisplayId(item)}
                      </span>
                      <span className="shrink-0 text-right font-black text-[var(--admin-text-primary)]">{formatCurrency(item.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="text-xs font-black text-[var(--admin-text-muted)]">
                    <tr>
                      <th className="pb-3 pr-4">{t('finance.abnormal.table.time')}</th>
                      <th className="px-4 pb-3">{t('finance.abnormal.table.order')}</th>
                      <th className="px-4 pb-3">{t('finance.abnormal.table.device')}</th>
                      <th className="px-4 pb-3">{t('finance.abnormal.table.status')}</th>
                      <th className="pb-3 pl-4 text-right">{t('finance.abnormal.table.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abnormalFinance.recent.map((item) => (
                      <tr key={item.id || item.externalId || item.payermax_order_id} className="border-t border-[var(--admin-border)]">
                        <td className="py-3 pr-4 text-[var(--admin-text-muted)]">{formatAbnormalTime(item.completedAt || item.createdAt)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--admin-text-primary)]">{item.payermax_order_id || item.externalId || item.recordId || item.id || '-'}</td>
                        <td className="px-4 py-3 text-[var(--admin-text-primary)]" title={item.deviceId || item.unit?.deviceId || ''}>
                          {getDeviceDisplayId(item)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-lg bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">
                            {t(`finance.abnormal.status.${item.status}`, { defaultValue: item.status || '-' })}
                          </span>
                        </td>
                        <td className="py-3 pl-4 text-right font-black text-[var(--admin-text-primary)]">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {abnormalFinance.totalCount > 0 && (
            <div className="mt-4 flex items-center justify-end gap-3 text-sm">
              <button type="button" disabled={abnormalPage <= 1 || loading} onClick={() => setAbnormalPage((value) => Math.max(1, value - 1))} className="rounded-lg border border-[var(--admin-border)] px-3 py-2 font-bold disabled:cursor-not-allowed disabled:opacity-50">
                {t('finance.pagination.previous')}
              </button>
              <button type="button" disabled={abnormalPage >= abnormalTotalPages || loading} onClick={() => setAbnormalPage((value) => Math.min(abnormalTotalPages, value + 1))} className="rounded-lg border border-[var(--admin-border)] px-3 py-2 font-bold disabled:cursor-not-allowed disabled:opacity-50">
                {t('finance.pagination.next')}
              </button>
            </div>
          )}
        </div>
      </section>

      {canViewUnits && <section className="admin-card overflow-hidden rounded-lg" aria-label={t('finance.table.title')}>
        <div className="flex flex-col gap-2 border-b border-[var(--admin-border)] p-4 sm:p-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h4 className="break-words text-base font-black text-[var(--admin-text-primary)]">{t('finance.table.title')}</h4>
            <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{t('finance.table.subtitle', { month: selectedMonthLabel })}</p>
          </div>
          <span className="text-sm font-bold text-[var(--admin-text-muted)]">
            {loading ? t('common.loading') : formatUnitCount(t, deviceFinanceRows.length)}
          </span>
        </div>

        <div className="space-y-3 p-4 md:hidden">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('finance.table.loading')}</div>
          ) : deviceFinanceRows.length === 0 ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('finance.table.noUnits')}</div>
          ) : visibleDeviceFinanceRows.map((row) => (
            <article key={row.key} className="admin-muted-panel rounded-lg p-4">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="break-words text-sm font-black text-[var(--admin-text-primary)]">{row.deviceName}</p>
                  <p className="mt-1 break-all font-mono text-xs font-bold text-[var(--admin-accent)]" title={row.fullDeviceId}>{row.deviceId}</p>
                </div>
                <span className="shrink-0 rounded-lg bg-[var(--admin-surface)] px-2 py-1 text-xs font-black text-[var(--admin-text-muted)]">{row.period}</span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-[var(--admin-text-muted)]">{t('finance.table.revenue')}</span>
                  <span className="text-right font-black text-[var(--admin-text-primary)]">{formatCurrency(row.revenue)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[var(--admin-text-muted)]">{t('finance.table.transactionCount')}</span>
                  <span className="text-right font-black text-[var(--admin-text-primary)]">{formatInteger(row.transactionCount)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[var(--admin-text-muted)]">{t('finance.table.waterVolume')}</span>
                  <span className="text-right font-black text-[var(--admin-text-primary)]">{formatVolume(row.waterVolume)}</span>
                </div>
                <div className="pt-2 text-xs leading-5 text-[var(--admin-text-muted)]">
                  <span className="font-bold">{row.partner}</span>
                  <span className="mx-1">/</span>
                  <span>{row.location}</span>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[900px] text-left">
            <thead className="admin-muted-panel text-xs font-black text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('finance.table.device')}</th>
                <th className="p-4">{t('finance.table.partner')}</th>
                <th className="p-4 text-right">{t('finance.table.revenue')}</th>
                <th className="p-4 text-right">{t('finance.table.transactionCount')}</th>
                <th className="p-4 text-right">{t('finance.table.waterVolume')}</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr><td colSpan="5" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('finance.table.loading')}</td></tr>
              ) : deviceFinanceRows.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('finance.table.noUnits')}</td></tr>
              ) : visibleDeviceFinanceRows.map((row) => (
                <tr key={row.key} className="border-t border-[var(--admin-border)] transition-colors hover:bg-[var(--admin-surface-soft)]">
                  <td className="p-4">
                    <div className="max-w-[280px] break-words font-black text-[var(--admin-text-primary)]">{row.deviceName}</div>
                    <div className="mt-1 break-all font-mono text-xs font-bold text-[var(--admin-accent)]" title={row.fullDeviceId}>{row.deviceId}</div>
                  </td>
                  <td className="p-4">
                    <div className="max-w-[260px] break-words font-bold text-[var(--admin-text-primary)]">{row.partner}</div>
                    <div className="mt-1 max-w-[260px] break-words text-xs leading-5 text-[var(--admin-text-muted)]">{row.location}</div>
                    <div className="mt-1 text-xs font-bold text-[var(--admin-text-muted)]">{t('finance.table.period')}: {row.period}</div>
                  </td>
                  <td className="p-4 text-right text-base font-black text-[var(--admin-text-primary)]">{formatCurrency(row.revenue)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatInteger(row.transactionCount)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatVolume(row.waterVolume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && deviceFinanceRows.length > 0 && (
          <div className="flex items-center justify-end gap-3 border-t border-[var(--admin-border)] p-4 text-sm">
            <span className="mr-auto text-[var(--admin-text-muted)]">{t('finance.table.pageSummary', { page: devicePage, totalPages: deviceTotalPages || 1 })}</span>
            <button type="button" disabled={devicePage <= 1} onClick={() => setDevicePage((value) => Math.max(1, value - 1))} className="rounded-lg border border-[var(--admin-border)] px-3 py-2 font-bold disabled:cursor-not-allowed disabled:opacity-50">
              {t('finance.pagination.previous')}
            </button>
            <button type="button" disabled={devicePage >= deviceTotalPages} onClick={() => setDevicePage((value) => Math.min(deviceTotalPages, value + 1))} className="rounded-lg border border-[var(--admin-border)] px-3 py-2 font-bold disabled:cursor-not-allowed disabled:opacity-50">
              {t('finance.pagination.next')}
            </button>
          </div>
        )}
      </section>}
    </div>
  );
}
